import { FC, MouseEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useAtom } from 'jotai'
import moment from 'moment'
import useResizeObserver from 'use-resize-observer'

import Gallery from './Gallery'
import Sidebar from './Sidebar'
import ShareButton from './ShareButton'
import FavoriteButton from './FavoriteButton'
// import TheAcreExperience from '../TheAcreExperience'
import { formatStandardListingProperties } from '../ListingsGrid'
import TrackScrollDepth from '../TrackScrollDepth'

import { Listing, PhotoResolutions } from '../../services/getListings'
import {
  IndividualListing,
  useListingDetails,
} from '../../services/getListingById'
import { useTracking } from '../../services/context/TrackingContext'
import { usePropertyTracking } from '../../hooks/useAnalyticsIdentification'
import useAuth from '../../hooks/useAuth'

import { CloseLgIcon, ArrowLeftIcon } from 'icons'

import { CalculatorPageContent } from '../../hooks/useAcreCalculator'

import { customerFavoriteListings } from '../../utils/atoms'

const ListingContent: FC<{
  handleClose?: (e: MouseEvent<HTMLButtonElement>) => void
  handleBack?: (e: MouseEvent<HTMLButtonElement>) => void
  listingId: number
  listingInitialData?: Listing
  calculator: CalculatorPageContent
}> = ({
  handleClose,
  handleBack,
  listingId,
  listingInitialData,
  calculator,
}) => {
  const { t } = useTranslation(['homes', 'common'])
  const { track } = useTracking()
  const { user } = useAuth()
  const propertyTracking = usePropertyTracking(user?.email || '')
  const { data: fullListing, status: fullListingStatus } =
    useListingDetails(listingId)

  const listing: IndividualListing | Listing | null =
    fullListing ?? listingInitialData ?? null

  const timeOnMarket = listing
    ? moment().diff(moment(listing.list_date), 'days')
    : null
  const [galleryPhotos, setGalleryPhotos] = useState<PhotoResolutions[]>([])

  const [favoriteListings] = useAtom(customerFavoriteListings)

  useEffect(() => {
    if (fullListingStatus === 'success' && listing?.thumbnail_resolutions) {
      const newGalleryPhotos = fullListing?.photo_resolutions || []
      newGalleryPhotos[0] = listing?.thumbnail_resolutions

      setGalleryPhotos(newGalleryPhotos)
    }
  }, [
    fullListing?.photo_resolutions,
    fullListingStatus,
    listing?.thumbnail_resolutions,
  ])

  // Track property view when listing loads
  useEffect(() => {
    if (listing && propertyTracking && user) {
      propertyTracking.trackView(listingId.toString(), {
        source: 'direct',
        timeOnPage: 0 // Will be updated by scroll tracking
      })
    }
  }, [listing, listingId, propertyTracking, user])

  const mobileContainerRef = useRef<HTMLDivElement>(null)
  const { ref: galleryRef, height: galleryHeight = 0 } =
    useResizeObserver<HTMLDivElement>({
      box: 'border-box',
    })

  return (
    <>
      <header className="border-b">
        <div className="mx-auto flex max-w-1440 items-center justify-between px-16 py-18">
          <div className="flex items-center space-x-16">
            {handleBack ? (
              <button
                className="group rounded-full border border-grey-6 p-4 transition-colors hover:bg-grey-6"
                onClick={handleBack}
              >
                <span className="sr-only">Back Home</span>
                <ArrowLeftIcon
                  aria-hidden="true"
                  className="stroke-grey-6 transition-colors rect-24 group-hover:stroke-white"
                />
              </button>
            ) : null}
            {listing?.property_status === 'reserved' ||
            listing?.property_status === 'acre-bought' ? (
              <h2 className="rounded-sm bg-green-1 px-16 py-10 text-14 font-semibold leading-tight">
                Acre Reserve
              </h2>
            ) : (
              <h2 className="rounded-sm bg-green-1 px-16 py-10 text-14 leading-tight">
                {fullListing && fullListing.mlsIdx.soldDate
                  ? t(fullListing.mlsIdx.status, { ns: 'common' })
                  : t('ui.time-on-market', { ns: 'common' })}{' '}
                {fullListing && fullListing.mlsIdx.soldDate ? (
                  <span>
                    {moment(fullListing.mlsIdx.soldDate).format('M/D/YYYY')}
                  </span>
                ) : (
                  <span className="font-semibold">
                    {timeOnMarket}{' '}
                    {t('ui.day', { ns: 'common', count: timeOnMarket ?? 0 })}
                  </span>
                )}
              </h2>
            )}
          </div>

          <div className="ml-16 flex flex-row-reverse items-center">
            {handleClose ? (
              <button className="ml-16" onClick={handleClose}>
                <span className="sr-only">Close</span>
                <CloseLgIcon
                  aria-hidden="true"
                  className="stroke-grey-6 rect-38"
                />
              </button>
            ) : null}

            <ShareButton
              onCopy={() =>
                track({
                  event: 'Property Listing Link Copied',
                  properties: listing
                    ? formatStandardListingProperties(listing)
                    : {},
                })
              }
            />
            {listing?.live && (
              <div>
                <FavoriteButton
                  listingId={listing.id}
                  active={favoriteListings
                    .map((l) => l.id)
                    .includes(listing.id)}
                  trackLinkLocation="Listing Detail"
                  propertyData={{
                    price: listing.list_price,
                    bedrooms: listing.beds,
                    bathrooms: listing.baths,
                    location: listing.address
                  }}
                  onSave={() => {
                    track({
                      event: 'Property Listing Saved',
                      properties: {
                        ...formatStandardListingProperties(listing),
                        save_link_type: 'Icon',
                        save_action_description: 'Tap Heart Icon',
                        save_link_location: 'Listing Detail',
                      },
                    })
                  }}
                  className="mr-8 block"
                />
              </div>
            )}
          </div>
        </div>
      </header>
      <div
        className="relative mx-auto w-full max-w-1440 grow overflow-auto lg:grid lg:grid-cols-2 lg:overflow-hidden"
        ref={mobileContainerRef}
      >
        <TrackScrollDepth
          ref={galleryRef}
          tag="aside"
          className="sticky top-0 overflow-auto lg:static lg:border-r lg-d:border-b"
          thresholds={[0.05, 0.25, 0.5, 0.75, 1]}
          event="Property Listing Images Scrolled"
          properties={listing ? formatStandardListingProperties(listing) : {}}
          thresholdPropertyName="scroll_percentage"
          useScrollXMediaQuery="(max-width: 1023px)"
        >
          <Gallery
            listing={listing}
            photos={galleryPhotos}
            fullListingStatus={fullListingStatus}
          />
        </TrackScrollDepth>

        <Sidebar
          listing={listing}
          detailedListing={fullListing ?? null}
          calculator={calculator}
          mobileContainerRef={mobileContainerRef}
          galleryHeight={galleryHeight}
        />
      </div>
    </>
  )
}

export default ListingContent
